import PageSkeleton from "@/components/page-skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ListingPopulatingBadge from "@/components/listing/listing-populating-badge";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useListings } from "@/contexts/listings-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { convertDateToDistanceNow } from "@/lib/date";
import { cn } from "@/lib/utils";
import { ListingDTO } from "@shaarp/common";
import { NavLink } from "react-router-dom";

export default function ListingsList({
  listings,
  isLoading,
}: {
  listings: ListingDTO[];
  isLoading: boolean;
}) {
  const { t, currentLanguage } = useTranslation();
  const { populatingListingsIds } = useListings();

  return (
    <div className="flex flex-col gap-2 p-0 pt-0">
      {isLoading && <PageSkeleton />}
      {!isLoading && listings.length === 0 && (
        <div className="flex justify-center items-center p-4 h-full">
          <p className="text-sm text-muted-foreground">
            {t("listing.page.list.badges.empty")}
          </p>
        </div>
      )}
      {!isLoading &&
        listings.length > 0 &&
        listings.map((listing) => {
          // Still being filled: opening it now would show an empty list.
          const isPopulating = populatingListingsIds.has(listing.id);

          return (
          <NavLink
            key={listing.id}
            to={`/listing/${listing.id}`}
            aria-disabled={isPopulating}
            tabIndex={isPopulating ? -1 : undefined}
            className={cn(
              "flex overflow-hidden flex-col gap-2 items-start p-3 text-sm text-left rounded-lg border transition-all hover:bg-accent active:bg-accent",
              isPopulating && "pointer-events-none opacity-60",
            )}
          >
            <div className="flex flex-col gap-1 w-full">
              <div className="flex items-center">
                <div className="flex gap-2 items-center">
                  <div className="font-semibold">{listing.title}</div>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="ml-auto text-xs text-right">
                      {convertDateToDistanceNow(listing.lastUpdatedAt, {
                        languageOverride: currentLanguage,
                      })}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("listing.page.list.lastUpdate")}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="text-xs line-clamp-2 text-muted-foreground">
              {listing.description.substring(0, 80)}
            </div>
            <div className="flex gap-2 justify-between w-full">
              {isPopulating ? (
                <ListingPopulatingBadge />
              ) : listing.companiesCount === 0 ? (
                <Badge variant="outline">
                  {t("listing.page.list.badges.empty")}
                </Badge>
              ) : (
                <Badge variant="default">
                  {t("listing.page.list.badges.companies", {
                    count: listing.companiesCount,
                  })}
                </Badge>
              )}
              {listing.assignees && listing.assignees.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex -space-x-1">
                      {listing.assignees.slice(0, 3).map((assignee) => (
                        <Avatar
                          key={assignee.id}
                          className="w-6 h-6 border border-background"
                        >
                          <AvatarImage src={assignee.imageUrl} />
                          <AvatarFallback>
                            {assignee.fullName?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      ))}
                      {listing.assignees.length > 3 && (
                        <Avatar className="w-6 h-6 border border-background">
                          <AvatarFallback>
                            +{listing.assignees.length - 3}
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {listing.assignees.map((a) => a.fullName).join(", ")}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </NavLink>
          );
        })}
    </div>
  );
}
